import * as React from 'react';
import { throttle } from 'lodash';
import cx from 'classnames';

import './styles/ScrollContainer.scss';

type Props = {
  scrollToBottom?: boolean;
  onScroll?: () => void;
  children?: React.ReactNode;
  maxHeight?: number;
  horizontalScroll?: boolean;
};

type State = {
  scrollBottom: number;
};

class ScrollContainer extends React.Component<Props, State> {
  state = {
    scrollBottom: 0,
  };

  componentDidMount() {
    this.handleScroll = throttle(this.handleScroll, 100);
    if (this.props.scrollToBottom) {
      this.scrollToBottom();
    }
  }

  componentDidUpdate() {
    if (this.state.scrollBottom === 0) {
      if (this._container && this.props.scrollToBottom) {
        this._container.scrollTop = this._container.scrollHeight;
      }
    }
  }

  _container: HTMLElement | null = null;

  handleScroll = () => {
    if (this._container) {
      const scrollBottom =
        this._container.scrollTop + this._container.offsetHeight - this._container.scrollHeight;
      this.setState({ scrollBottom }, this.props.onScroll);
    }
  };

  isScrolledToBottom(offset: number = 0): boolean {
    if (this._container) {
      const { scrollTop } = this._container;
      const scrollBottom = scrollTop + this._container.offsetHeight - this._container.scrollHeight;
      return scrollBottom + offset >= 0;
    }
    return false;
  }

  scrollToBottom() {
    this.setState({ scrollBottom: 0 }, this.props.onScroll);
  }

  scrollToTop() {
    if (this._container) {
      this._container.scrollTop = 0;
    }
  }

  // This prevents mousewheel events on ScrollableArea instances from transferring to mousewheel
  // on the document's main scroll area.
  handleWheel = (event: React.WheelEvent) => {
    const node = this._container;
    if (!node) {
      return;
    }

    if (event.deltaX && !event.deltaY) {
      return;
    }

    const { scrollHeight, clientHeight } = node;
    if (scrollHeight > clientHeight) {
      const { scrollTop } = node;
      const delta = -event.deltaY;
      if (
        (delta > 0 && scrollTop === 0) ||
        (delta < 0 && scrollTop >= scrollHeight - clientHeight - 1)
      ) {
        event.preventDefault();
      }
    }
  };

  render() {
    return (
      <div
        className={cx('ScrollContainer', {
          'ScrollContainer--horizontal': this.props.horizontalScroll,
        })}
        ref={(container) => {
          this._container = container;
        }}
        onScroll={this.handleScroll}
        onWheel={this.handleWheel}
        style={this.props.maxHeight ? { maxHeight: this.props.maxHeight } : {}}
      >
        {this.props.children}
      </div>
    );
  }
}

export default ScrollContainer;
